import { z } from 'zod';
import { EMAIL_PROVIDERS, EMAIL_SENDER_KINDS, NOTIFICATION_CHANNELS, NOTIFICATION_STATUSES, NOTIFICATION_TEMPLATES, SMS_DELIVERY_STATUSES } from '@bulk/types';
import { cursorSchema, idSchema, keysetPageSchema, paginationQuerySchema, patchOf } from './common';

export const notificationSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  userId: idSchema.nullable(),
  memberId: idSchema.nullable(),
  channel: z.enum(NOTIFICATION_CHANNELS),
  template: z.enum(NOTIFICATION_TEMPLATES),
  subject: z.string(),
  body: z.string(),
  status: z.enum(NOTIFICATION_STATUSES),
  readAt: z.string().nullable(),
  sentAt: z.string().nullable(),
  createdAt: z.string(),
});
export type NotificationDto = z.infer<typeof notificationSchema>;

export const emailSenderSchema = z.object({
  via: z.enum(EMAIL_SENDER_KINDS),
  provider: z.enum(EMAIL_PROVIDERS),
  from: z.string(),
  replyTo: z.string().nullable(),
});
export type EmailSenderDto = z.infer<typeof emailSenderSchema>;

export const emailLogEntrySchema = notificationSchema.extend({
  error: z.string().nullable(),
  attempts: z.number().int(),
  nextAttemptAt: z.string().nullable(),
  sender: emailSenderSchema.nullable(),
});
export type EmailLogEntryDto = z.infer<typeof emailLogEntrySchema>;

export const smsDeliverySchema = z.object({
  status: z.enum(SMS_DELIVERY_STATUSES),
  reason: z.string().nullable(),
  reportedAt: z.string(),
});

export const smsLogEntrySchema = notificationSchema.extend({
  recipient: z.string().nullable(),
  memberName: z.string().nullable(),
  error: z.string().nullable(),
  attempts: z.number().int(),
  nextAttemptAt: z.string().nullable(),
  delivery: smsDeliverySchema.nullable(),
});
export type SmsLogEntryDto = z.infer<typeof smsLogEntrySchema>;

export const smsLogQuerySchema = paginationQuerySchema.extend({
  problems: z.stringbool().optional(),
});
export type SmsLogQuery = z.infer<typeof smsLogQuerySchema>;

export const staffNotificationPrefsSchema = z.object({
  email: z.boolean(),
  sms: z.boolean(),
});
export type StaffNotificationPrefs = z.infer<typeof staffNotificationPrefsSchema>;

export const updateStaffNotificationPrefsSchema = patchOf(staffNotificationPrefsSchema);
export type UpdateStaffNotificationPrefsInput = z.infer<typeof updateStaffNotificationPrefsSchema>;

export const staffNotificationSettingsSchema = staffNotificationPrefsSchema.extend({
  smsAvailable: z.boolean(),
  hasPhone: z.boolean(),
});
export type StaffNotificationSettingsDto = z.infer<typeof staffNotificationSettingsSchema>;

export const notificationListQuerySchema = paginationQuerySchema.extend({
  unreadOnly: z.stringbool().optional(),
  cursor: cursorSchema.optional(),
});

export const notificationPageSchema = keysetPageSchema(notificationSchema);
export type NotificationPage = z.infer<typeof notificationPageSchema>;

export const notificationPreferencesSchema = z.object({
  email: z.boolean().default(true),
  classReminders: z.boolean().default(true),
  membershipUpdates: z.boolean().default(true),
  marketing: z.boolean().default(false),
});
export type NotificationPreferences = z.infer<typeof notificationPreferencesSchema>;

export const auditLogSchema = z.object({
  id: idSchema,
  gymId: idSchema.nullable(),
  actorUserId: idSchema.nullable(),
  actorEmail: z.string().nullable(),
  action: z.string(),
  entity: z.string(),
  entityId: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()),
  requestId: z.string().nullable(),
  createdAt: z.string(),
});
export type AuditLogDto = z.infer<typeof auditLogSchema>;

export const auditLogPageSchema = keysetPageSchema(auditLogSchema);

export const auditListQuerySchema = paginationQuerySchema.extend({
  entity: z.string().optional(),
  action: z.string().optional(),
  cursor: cursorSchema.optional(),
});
